import { Component, Input, OnChanges, OnDestroy, ChangeDetectorRef, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Chart, ChartType, ChartDataset, ChartEvent} from 'chart.js';
import { NWMChartSettings } from './NWMChartSettings';
import { APIService } from 'app/services/api.service';
import { AuthenticationService } from 'app/services/auth.service';
import { LoaderService } from "app/services/loader.service";
import { tooltipType } from "app/components/NWMCharts/NWMCharts.module";
import { Observable, Subscription, combineLatest } from 'rxjs';
import { AppConfig } from "app/app.config";
import { NgTemplateOutlet, NgClass, NgStyle, DecimalPipe, PercentPipe, CurrencyPipe } from '@angular/common';
import { BaseChartDirective } from 'ng2-charts';
import { MinusSignToParens } from 'app/pipes/minus-sign-to-parens.pipe';

type customAssetDetailsType = {
  name: string;
  details: assetDetailsType[];
  total: number;
  totalPercent: string;
};

type assetDetailsType = {
  amount?: number;
  cad: number;
  localpercent?: string;
  usd: number;
  ProductName: string;
};

type assetAllocationType = {
  AssetSubClass: string;
  Details: assetDetailsType[];
  MarketValue: number;
  percent: number;
  PercentInAssetSubClass: string;
  ReportColor: string;
  Url: string;
};

type assetApiDetailsType = {
  cad: number;
  usd: number;
  ProductName: string;
};

type assetAllocationApiDataType = {
  AssetSubClass: string;
  Details: assetApiDetailsType[];
  MarketValue: number;
  PercentInAssetSubClass: string;
  ReportColor: string;
  Url: string;
};

type assetAllocationApiType = {
  data: assetAllocationApiDataType[];
  reportColor: string[];
  TotalMarketValue: number;
};

type portfolioCompositionType = {
  AssetSubClass: string;
  MarketValue: null | number;
  PercentInAssetSubClass: null | number;
  ReportColor: string;
  Url: string;
};

type assetAllocationGroupApiDataType = {
  data: portfolioCompositionType[];
};

type usdExposureApiDataType = {
  TotalUSDExposure: number;
  USDExposurePercentage: number;
}[];

type chartEventType = {event?: ChartEvent; active?: object[] };

@Component({
    selector: 'PortfolioAllocation',
    templateUrl: './PortfolioAllocation.component.html',
    styleUrls: ['./nwmchart.component.scss'],
    imports: [NgTemplateOutlet, NgClass, NgStyle, BaseChartDirective, MinusSignToParens, DecimalPipe, PercentPipe, CurrencyPipe]
})

export class PortfolioAllocationComponent implements OnChanges, OnDestroy {
  private api = inject(APIService);
  private auth = inject(AuthenticationService);
  private loaderService = inject(LoaderService);
  private router = inject(Router);
  private config = inject(AppConfig);
  private ref = inject(ChangeDetectorRef);

  @Input() plan?: string;

  public dataLoaded = false;
  public chartData: ChartDataset[] = [{data:[],backgroundColor:[]}];
  public chartLabels: string[] = [];
  public chartType: ChartType = 'doughnut';
  public chartLegend = false;
  public tooltip: tooltipType = {visible:false, rows:[]};
  public chartSettings: NWMChartSettings;

  public total?: number;

  public portfolioDetails?: assetAllocationType[];
  public portfolioMaxPercent?: number;
  public portfolioComposition: portfolioCompositionType[] = [];
  public usdExposure?: number;
  public usdExposurePercentage?: number;
  public assetDetails?: customAssetDetailsType = undefined;
  public subscription?: Subscription;
  public isPershing = false;

  constructor() {
    this.chartSettings = new NWMChartSettings();
  }

  ngOnChanges(): void {
    this.dataLoaded = false;
    this.tooltip.visible = false;
    this.chartSettings.options.plugins = {tooltip:{enabled: false, external: (a)=>this.customTooltip(a.chart)}};
    this.chartSettings.options.scales = undefined;
    this.chartSettings.options.elements = {arc: {borderWidth: 0}};

    const reRg = this.auth.getCurrentReRg()!;
    this.isPershing = reRg.IsPershing;
    this.loadChart();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  customTooltip(chart: Chart): void {
    const tooltipModel = chart.tooltip!;
    this.tooltip.visible = tooltipModel.opacity !== 0;
    if (tooltipModel.body) {
      const i = tooltipModel.dataPoints[0].dataIndex;
      this.tooltip.rows = [
        { key: this.portfolioDetails![i].AssetSubClass + ':', value: this.portfolioDetails![i].PercentInAssetSubClass },
      ];
    }
    setTimeout(()=>{this.ref.detectChanges();});
  }

  chartClicked(e: chartEventType) {
    const i = (e.active![0] as {index:number}).index;
    if (e.active && e.active[0] && this.portfolioDetails![i].Url && !this.isPershing) {
      const url = this.portfolioDetails![i].Url;
      if (url.indexOf('resource/plannings')>-1) {
        this.router.navigate(['/resource/plannings']);
      } else {
        const a = document.createElement('a');
        document.body.appendChild(a);
        a.setAttribute('href', url);
        a.setAttribute('target', '_blank');
        a.click();
        a.parentNode!.removeChild(a);
      }
    }
  }

  loadChart() {
    this.loaderService.display(true, 'PortfolioAllocation');
    this.usdExposure = undefined;
    this.usdExposurePercentage = undefined;

    const reRg = this.auth.getCurrentReRg()!;
    const vd = this.auth.getCurrentValuationDate();
    const planPath = this.api.getPlanPath(this.plan || '', reRg.IsPershing);

    const observables: Observable<unknown>[] = [
      this.api.get<assetAllocationApiType>(`${this.config.portfolio.portfolioAssetAllocationUrl}/${reRg.ReRgId}/${vd}/${planPath}`),
      this.api.get<assetAllocationGroupApiDataType>(`${this.config.portfolio.portfolioAssetAllocationGroupUrl}/${reRg.ReRgId}/${vd}/${planPath}`)
    ];

    if (this.plan == '' && !this.isPershing) {
      observables.push(this.api.get<usdExposureApiDataType>(`${this.config.portfolio.portfolioUSDExposureUrl}/${reRg.ReRgId}/${vd}`));
    }

    this.subscription = combineLatest(observables).subscribe(allData => {
      const data1 = allData[0] as assetAllocationApiType;

      this.chartLabels = [];

      this.portfolioMaxPercent = 0;
      this.portfolioDetails = data1.data.map(portfolioDetail => ({ ...portfolioDetail, percent: 0 }))
      this.chartData[0].data = [];

      for (let i = 0; i < data1.data.length; i++) {
        this.portfolioDetails![i].percent = parseFloat(data1.data[i].PercentInAssetSubClass);
        if (this.portfolioDetails![i].percent > this.portfolioMaxPercent) this.portfolioMaxPercent = this.portfolioDetails[i].percent;
        this.chartLabels.push(data1.data[i].AssetSubClass);
        this.chartData[0].data.push(this.portfolioDetails![i].percent);
      }

      this.chartData[0].backgroundColor = data1.reportColor;
      this.total = data1.TotalMarketValue;

      this.dataLoaded = true;

      const data2 = allData[1] as assetAllocationGroupApiDataType;
      this.portfolioComposition = data2.data;

      if (allData[2]) {
        const data3 = allData[2] as usdExposureApiDataType;
        if (data3[0] && typeof data3[0].USDExposurePercentage == 'number') {
          this.usdExposure = data3[0].TotalUSDExposure;
          this.usdExposurePercentage = data3[0].USDExposurePercentage;
        }
      }

      this.loaderService.display(false, 'PortfolioAllocation');
    },
    () => {
      this.loaderService.display(false, 'PortfolioAllocation');
    });
  }

  onAssetClick(name: string, total: number, totalPercent: string, assetDetails: assetDetailsType[]) {
    this.assetDetails = undefined;
    if (assetDetails) {
      for (const asset of assetDetails) {
        if (this.isPershing) {
          asset.amount = asset.usd;
        } else {
          asset.amount = asset.cad;
        }
        const percent = (asset.amount / total * parseFloat(totalPercent.substring(0, totalPercent.length - 1))).toFixed(2) + '%';
        if (percent !== 'NaN%') {
          asset.localpercent = percent;
        } else {
          asset.localpercent = '';
        }
      }
    }
    setTimeout(() => {this.assetDetails = { name, details: assetDetails, total, totalPercent }});
  }
}